import {
  RegistrationActivity,
  FrontendRegistrationGroup,
} from "@timeedit/registration-schemas";
import {
  NO_GROUP_ID,
  NO_GROUP_NUMBER,
  isDefined,
  presentGroupName,
} from "@timeedit/registration-shared";
import { Layout } from "@timeedit/ui-components/antd";
import cn from "classnames";
import { uniqBy } from "lodash-es";
import { useCallback, useContext, useMemo } from "react";
import { EventPropGetter, ViewsProps } from "react-big-calendar";
import { CalendarEvent, createEvents } from "../../../utils/events";
import GroupListView from "../Calendar/CalendarListView/GroupListView";
import { GroupModalContent } from "../Calendar/CalendarModalContent/GroupModalContent";
import RegistrationCalendar from "../Calendar/RegistrationCalendar";
import {
  IdContext,
  LoadActionContext,
  RegistrationContext,
  UtilContext,
} from "../RegistrationContexts";
import { RegistrationOptionsContext } from "../RegistrationOptions";
import { createGroupNumbers, getColorIndex, getGroupName } from "../utils";
import "./groupPage.scss";
import { BackButton } from "./BackButton";
import { GroupPageTitle } from "./GroupPageTitle";

export function GroupPage({
  focusRef,
}: {
  focusRef?: React.RefObject<HTMLInputElement>;
}): React.JSX.Element {
  const { mapping, translations, navigate, analyticsEvent } =
    useContext(UtilContext);
  const { registration, mode } = useContext(RegistrationContext);
  const { id, studentId } = useContext(IdContext);
  const loadActions = useContext(LoadActionContext);
  const { slots, isDesktop, listOnly, headingLevel } = useContext(
    RegistrationOptionsContext
  );

  const views = createViews();

  const activity: RegistrationActivity | undefined = isDefined(id)
    ? registration.activities?.[id]
    : undefined;

  const reservations = (registration.events ?? [])
    .filter(isDefined)
    .filter((event) => activity?.children.includes(event.groupId));

  const course = isDefined(activity)
    ? registration.courses[activity.parentId]
    : undefined;

  const events = createEvents({
    reservations,
  });

  const eventPropGetter = createEventPropGetter(events);

  const titleLevel = headingLevel(2);
  const groupPageTitleSemanticLevel = isDesktop ? headingLevel(3) : titleLevel;

  return (
    <Layout
      className={cn("groupPage", { "groupPage--desktop": isDesktop })}
      tabIndex={-1}
      ref={focusRef}
    >
      <div className="groupPage__header">
        {!isDesktop && <BackButton handleGoBack={handleGoBack} />}
        <GroupPageTitle
          id="activity-header"
          semanticLevel={titleLevel}
          course={course}
          activity={activity}
        />
        <div className="groupPage__status">
          {isDefined(activity) &&
            slots.activityBadge({
              signal: activity.allocationSignal,
              translations,
              registration,
              activityId: activity.id,
            })}
        </div>
      </div>
      <div className="groupPage__content">
        <RegistrationCalendar
          activityId={id}
          mode={mode}
          translations={translations}
          mapping={mapping}
          registration={registration}
          extraEvents={events}
          views={views}
          title={
            <GroupPageTitle
              course={course}
              semanticLevel={groupPageTitleSemanticLevel}
              activity={activity}
            />
          }
          eventModalContent={GroupModalContent}
          eventPropGetter={eventPropGetter}
          loadActions={loadActions}
          analyticsEvent={analyticsEvent}
          listOnly={listOnly}
          isDesktop={isDesktop}
        />
      </div>
    </Layout>
  );

  function createEventPropGetter(
    coloredEvents: CalendarEvent[]
  ): EventPropGetter<CalendarEvent> {
    const groupNumbers = createGroupNumbers({
      events: coloredEvents,
      mapping,
      groups: registration.groups,
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback<EventPropGetter<CalendarEvent>>(
      (event, _start, _end, _isSelected) => {
        if (event.data.groupId === NO_GROUP_ID) {
          event.title = <div>{event.data.name}</div>;
          return {
            className: "rbc-event--extra",
          };
        }
        const group = registration.groups[event.data.groupId];
        if (!isDefined(group)) {
          return {};
        }
        const allocationStatus = group.allocationStatus;

        const createBody = makeCreateBody({
          group,
          event,
          hasConflict: event.data.hasConflict,
        });
        const groupName = getGroupName(group, mapping);

        if (allocationStatus === "ALLOCATED_TO_THIS") {
          const eventOnCurrentAllocObject = events.some(
            (e) => e.id === event.id
          );
          if (eventOnCurrentAllocObject) {
            const groupNumberIndex = groupNumbers.findIndex(
              (groupNumber) => groupNumber === groupName
            );
            const colorIndex =
              groupNumberIndex === -1
                ? -1
                : getColorIndex({ index: groupNumberIndex });

            const titleText = createTitleText(group);

            event.title = <span className="grouplabel">{titleText}</span>;
            const body = createBody();
            event.body = body;
            event.data.groupNumber = groupNumberIndex;
            if (colorIndex === -1)
              return {
                className: "rbc-event--other",
              };
            event.footer = {
              wantToShowSuccess: true,
              wantToShowConflict: event.data.hasConflict,
              wantToShowWaitlist: group.waitlistPosition > 0,
            };
            return {
              className: `rbc-event--success`,
            };
          }
          const body = createBody({ showCourses: true });
          event.title = body;
          event.data.groupNumber = NO_GROUP_NUMBER;

          return {
            className: "rbc-event--other",
          };
        }

        if (allocationStatus === "ALLOCATED_TO_OTHER") {
          const groupNumberIndex = groupNumbers.findIndex(
            (groupNumber) => groupNumber === groupName
          );
          const colorIndex =
            groupNumberIndex === -1
              ? -1
              : getColorIndex({ index: groupNumberIndex });

          const titleText = createTitleText(group);

          event.title = <span className="grouplabel">{titleText}</span>;
          const body = createBody();
          event.body = body;
          event.data.groupNumber = groupNumberIndex;
          if (colorIndex === -1)
            return {
              className: "rbc-event--other",
            };
          event.footer = {
            wantToShowSuccess: false,
            wantToShowConflict: event.data.hasConflict,
            wantToShowWaitlist: group.waitlistPosition > 0,
          };
          if (group.open === false) {
            event.footer.text = translations.closed;
            return { className: "rbc-event--closed" };
          }
          if (group.seats.available <= 0 && !registration.enableWaitlist) {
            event.footer.text = translations.noSeatsLeft;
            return { className: "rbc-event--no-seats" };
          }

          return { className: `rbc-event--${colorIndex}` };
        }

        if (allocationStatus === "MULTIPLE_ALLOCATIONS") {
          const group = registration.groups[event.data.groupId];
          const activityEventId = isDefined(group)
            ? registration.activities[group.parentId]?.id
            : null;
          if (id === activityEventId) {
            const groupNumberIndex = groupNumbers.findIndex(
              (groupNumber) => groupNumber === groupName
            );
            event.data.groupNumber = groupNumberIndex;
            event.title = <span className="grouplabel">{groupName}</span>;
          } else {
            event.data.groupNumber = NO_GROUP_NUMBER;
          }

          const body = createBody();
          event.body = body;

          return {
            className: cn(
              "rbc-event--base--error",
              "rbc-event__border",
              "rbc-event__border--red"
            ),
          };
        }

        if (allocationStatus === "NOT_ALLOCATED") {
          const groupNumberIndex = groupNumbers.findIndex(
            (groupNumber) => groupNumber === groupName
          );
          const colorIndex =
            groupNumberIndex === -1
              ? -1
              : getColorIndex({ index: groupNumberIndex });
          event.data.groupNumber = groupNumberIndex;

          if (colorIndex === -1)
            return {
              className: "rbc-event--other",
            };
          const body = createBody();

          const titleText = createTitleText(group);
          event.title = <span className="grouplabel">{titleText}</span>;
          event.footer = {
            wantToShowSuccess: false,
            wantToShowConflict: event.data.hasConflict,
            wantToShowWaitlist: group.waitlistPosition > 0,
          };
          event.body = body;
          if (group.open === false) {
            event.footer.text = translations.closed;
            return { className: "rbc-event--no-seats" };
          }
          if (group.seats.available <= 0 && !registration.enableWaitlist) {
            event.footer.text = translations.noSeatsLeft;
            return { className: "rbc-event--no-seats" };
          }
          return { className: `rbc-event--${colorIndex}` };
        }

        return {};
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [registration, reservations]
    );
  }

  type MakeCreateTitleProps = {
    event: CalendarEvent;
    group: FrontendRegistrationGroup;
  } & Pick<CalendarEvent["data"], "hasConflict">;
  function makeCreateBody({
    event,
    group,
  }: MakeCreateTitleProps): (props?: {
    showCourses: boolean;
  }) => React.JSX.Element {
    return function createBody(props?: { showCourses: boolean }) {
      if (!isDefined(props) || !props.showCourses) {
        return <></>;
      }
      const courseTypes = mapping.getIds("course");
      const courses = Object.values(registration.courses);
      const reservationObjects = uniqBy(
        event.data.reservations
          .flatMap((reservation) => reservation.objects)
          .filter(isDefined),
        "objectId"
      );
      const courseText = reservationObjects
        ?.filter((o) => courseTypes.includes(o.typeId))
        .map((o) => courses.find((c) => c?.id === o.objectId))
        .filter(isDefined)
        .map((o) => mapping.string("courseLabel", o.teObject));
      return (
        <>
          <div>{courseText}</div>
          <div>{mapping.parse("activityType", group.teObject)}</div>
        </>
      );
    };
  }

  function createViews(): ViewsProps<CalendarEvent> {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
      () => ({
        week: true,
        work_week: true,
        agenda: GroupListView,
      }),
      []
    );
  }

  function handleGoBack(): void {
    navigate(studentId ? "" : "/registration");
  }

  function createTitleText(group: FrontendRegistrationGroup): string {
    return presentGroupName(mapping, group);
  }
}
